import React, { useEffect, useMemo, useState, forwardRef, useImperativeHandle } from 'react';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Checkbox from '@mui/material/Checkbox';
import CheckIcon from '@mui/icons-material/Check';
import CircleIcon from '@mui/icons-material/Circle';
import Tooltip from '@mui/material/Tooltip';
import { SensorLatest } from '../../services/api';
import { getActiveMarker, getSensorsById } from '../../state/selectors';
import { getBandColor, SensorFilteredStats, PlotType } from './helpers';
import useGlobalStyles from '../../styles';
import useStyles from '../../styles/calendarView';
import { setSelectedSensors } from '../../state/actions';
import HistogramPlot from '../Plots/HistogramPlot';
import ReportsAddCircleIcon from '../../styles/icons/ReportsAddCircleIcon';
import { useAppDispatch, useAppSelector } from '../../state/store';
import { themeProps } from '../../styles/theme';
import { TagSummaryRef } from './CalendarViewDataContainer';
import ValuePill from './ValuePill';
import { combineStats, Stats } from '../../utils/statistics';
import InlineBarPlot from '../Plots/InlineBarPlot';
import { SpaceParams } from '../Map/mapHelpers';
import useLocAllSpaces from '../Map/useLocAllSpaces';

interface TagViewSummaryProps {
  sensorFilteredStats: Map<string, SensorFilteredStats>;
  valuesRange: [number, number];
  summaryType: PlotType;
}

const TagListItem = React.memo(
  ({
    space,
    allSpaces,
    summaryValue,
    color,
    valuesRange,
    selectedSpaces,
    setSelectedSpaces,
    sensorFilteredStats,
    showHistogram,
  }: {
    space: SpaceParams;
    allSpaces: SpaceParams[];
    summaryValue: number | undefined;
    color: string;
    valuesRange: [number, number];
    selectedSpaces: string[];
    setSelectedSpaces: (value: string[]) => void;
    sensorFilteredStats: Map<string, SensorFilteredStats>;
    showHistogram: boolean;
  }) => {
    const theme = useTheme();
    const activeMarker = useAppSelector(getActiveMarker);
    const dispatch = useAppDispatch();
    const [isHovered, setIsHovered] = useState(false);

    const handleMouseEnter = (spaceItem: SpaceParams) => {
      setIsHovered(true);
      dispatch(setSelectedSensors(spaceItem.sensors));
    };

    const handleMouseLeave = () => {
      setIsHovered(false);
      if (selectedSpaces) {
        const spaceSensors: string[] = [];

        selectedSpaces.forEach((spaceId: string) => {
          const spaceDetail = allSpaces.find((item) => item.title === spaceId);
          if (spaceDetail) spaceSensors.push(...spaceDetail.sensors);
        });
        dispatch(setSelectedSensors(spaceSensors));
      } else dispatch(setSelectedSensors([]));
    };

    const toggleCheckBox = (spaceName: string) => {
      const newSpaces = [...selectedSpaces];
      const spacePos = newSpaces.indexOf(spaceName);

      if (spacePos === -1) newSpaces.push(spaceName);
      else delete newSpaces[spacePos];
      setSelectedSpaces(newSpaces);
    };

    function getSpaceInlineValues(spaceItem: SpaceParams): number[] {
      const allInlineValues: (number | null | undefined)[] = [];

      spaceItem.sensors.forEach((sensorId: string) => {
        const sensorData = sensorFilteredStats.get(sensorId);
        const stats = sensorData?.stats;

        let inlineValues: number[] | undefined;

        if (stats?.totalAdded !== undefined) {
          inlineValues = stats.values?.totVals;
        } else if (stats?.utl !== undefined) {
          inlineValues = stats.values?.utlVals?.map((v: number) => v * 100);
        } else {
          inlineValues = stats?.values?.avgVals;
        }

        if (inlineValues) {
          allInlineValues.push(...inlineValues);
        }
      });

      // Filter out any null/undefined values and return clean number array
      return allInlineValues.filter(
        // eslint-disable-next-line no-restricted-globals
        (value): value is number => typeof value === 'number' && !isNaN(value)
      );
    }

    const combinedStats = useMemo(() => {
      if (showHistogram) return null;
      const allStats: Stats[] = [];
      space.sensors.forEach((sensor) => {
        const sensorStats = sensorFilteredStats.get(sensor)?.stats;
        if (sensorStats) allStats.push(sensorStats);
      });
      return combineStats(allStats);
    }, [sensorFilteredStats, showHistogram, space.sensors]);

    return (
      <ListItem
        key={space.title}
        onMouseEnter={() => handleMouseEnter(space)}
        onMouseLeave={handleMouseLeave}
        sx={{ backgroundColor: isHovered ? `${color}66` : 'transparent' }}
      >
        <Grid container spacing={1}>
          <Grid size={{ md: 1, sm: 1, xs: 2 }} sx={{ marginLeft: '5px', alignSelf: 'center' }}>
            <Checkbox
              icon={
                <CircleIcon
                  style={{
                    color: 'transparent',
                    borderRadius: '50%',
                    border: `solid 4px ${color ?? themeProps.colors.lightGrey}`,
                  }}
                />
              }
              checkedIcon={
                <CheckIcon
                  style={{
                    color: theme.palette.text.primary,
                    borderRadius: '50%',
                    border: `dashed 4px ${color ?? themeProps.colors.lightGrey}`,
                  }}
                />
              }
              checked={selectedSpaces !== undefined && selectedSpaces.indexOf(space.title) !== -1}
              onChange={() => toggleCheckBox(space.title)}
            />{' '}
          </Grid>
          <Grid size={{ md: 2, sm: 2, xs: 2 }} sx={{ alignSelf: 'center' }}>
            <Typography variant="body1">{space.title}</Typography>
          </Grid>
          <Grid
            size={{ md: 1, sm: 1, xs: 5 }}
            sx={{ textAlign: { xs: 'end', sm: 'center' }, alignSelf: 'center' }}
          >
            <ValuePill value={summaryValue} />
          </Grid>
          <Grid size={{ md: 6, sm: 6, xs: 10 }} sx={{ textAlign: 'center', alignSelf: 'center' }}>
            {showHistogram ? (
              <HistogramPlot
                values={getSpaceInlineValues(space) ?? []}
                sensorName={space.title}
                varName={activeMarker}
                xRange={valuesRange}
              />
            ) : (
              combinedStats &&
              !combinedStats.bandTimes?.has('Unknown') && (
                <InlineBarPlot
                  data={combinedStats}
                  sensorName={space.title ?? 'UNKNOWN'}
                  varName={activeMarker}
                />
              )
            )}
          </Grid>
        </Grid>
      </ListItem>
    );
  }
);

const TagViewSummary = forwardRef<TagSummaryRef, TagViewSummaryProps>(
  ({ sensorFilteredStats, valuesRange, summaryType }, ref): JSX.Element => {
    const { classes: globalClasses } = useGlobalStyles();
    const { classes } = useStyles();
    const dispatch = useAppDispatch();
    const sensorsById = useAppSelector(getSensorsById);
    const activeMarker = useAppSelector(getActiveMarker);
    const [selectedSpaces, setSelectedSpaces] = useState<string[]>();
    const [showHistogram, setShowHistogram] = useState(true);
    const allSpaces = useLocAllSpaces();

    const allTaggedSensors = Array.from(new Set(allSpaces.flatMap((item) => item.sensors)));

    useImperativeHandle(
      ref,
      () => ({
        clearState: () => {
          setSelectedSpaces([]);
        },
      }),
      []
    );

    // avoid clearing sensors selected from compare page
    useEffect(() => {
      if (selectedSpaces !== undefined) {
        const spaceSensors: string[] = [];
        selectedSpaces.forEach((spaceId) => {
          const spaceDetail = allSpaces.find((item) => item.title === spaceId);
          if (spaceDetail) spaceSensors.push(...spaceDetail.sensors);
        });
        dispatch(setSelectedSensors(spaceSensors));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSpaces]);

    const sensors = useMemo(
      () => allTaggedSensors.map((id) => sensorsById.get(id) ?? ({ id } as SensorLatest)),
      [allTaggedSensors, sensorsById]
    );

    const minMaxValue = useMemo(() => {
      let minValue: number | undefined;
      let maxValue: number | undefined;
      sensors.forEach((sensor) => {
        const min = sensorFilteredStats.get(sensor.id)?.stats.maxMinAvg.min;
        const max = sensorFilteredStats.get(sensor.id)?.stats.maxMinAvg.max;
        if (min !== undefined && !Number.isNaN(min)) {
          if (minValue === undefined) minValue = min;
          else if (min < minValue) minValue = min;
        }
        if (max !== undefined && !Number.isNaN(max)) {
          if (maxValue === undefined) maxValue = max;
          else if (max > maxValue) maxValue = max;
        }
      });
      return { minValue, maxValue };
    }, [sensorFilteredStats, sensors]);

    function getAverageSummaryValue(space: SpaceParams): number | undefined {
      const summaryValues = space.sensors.map(
        (sensorId: string) => sensorFilteredStats.get(sensorId)?.summaryValue
      );

      const filteredValues = summaryValues.filter(
        (value): value is number => value !== null && value !== undefined && !Number.isNaN(value)
      );

      const average =
        filteredValues.length > 0
          ? filteredValues.reduce((sum: number, value: number) => sum + value, 0) /
            filteredValues.length
          : undefined;

      return Number(average?.toFixed(1));
    }

    return (
      <div>
        <List>
          <ListItem sx={{ padding: '8px' }}>
            <Grid container spacing={1}>
              <Grid size={{ md: 1, sm: 1, xs: 2 }} className={classes.centered}>
                <Tooltip title="Add to compare" placement="top">
                  <div>
                    <ReportsAddCircleIcon />
                  </div>
                </Tooltip>
              </Grid>

              <Grid
                size={{ md: 2, sm: 2, xs: 2 }}
                sx={{ alignSelf: 'center' }}
                className={globalClasses.hideInSmallScreen}
              >
                <Typography variant="h6">Name</Typography>
              </Grid>
              <Grid
                size={{ md: 1, sm: 1 }}
                className={`${classes.centered} ${globalClasses.hideInSmallScreen}`}
              >
                <Typography variant="h6">{summaryType}</Typography>
              </Grid>
              <Grid
                size={{ md: 4, sm: 4, xs: 8 }}
                className={`${classes.centered} ${globalClasses.centeredFlex}`}
              >
                {minMaxValue.maxValue && <ValuePill value={minMaxValue.minValue} />}
                <Button
                  onClick={() => setShowHistogram(!showHistogram)}
                  variant={themeProps.btnVariant.default}
                >
                  {showHistogram ? 'Histogram' : 'Inline'}
                </Button>
                {minMaxValue.maxValue && <ValuePill value={minMaxValue.maxValue} />}
              </Grid>

              {allSpaces.map((space) => {
                const summaryValue = getAverageSummaryValue(space);
                const color = !Number.isNaN(summaryValue)
                  ? getBandColor(getAverageSummaryValue(space), activeMarker)
                  : themeProps.colors.lightGrey;

                return (
                  <TagListItem
                    space={space}
                    allSpaces={allSpaces}
                    summaryValue={summaryValue}
                    color={color}
                    valuesRange={valuesRange}
                    selectedSpaces={selectedSpaces ?? []}
                    setSelectedSpaces={setSelectedSpaces}
                    sensorFilteredStats={sensorFilteredStats}
                    showHistogram={showHistogram}
                  />
                );
              })}
            </Grid>
          </ListItem>
        </List>
      </div>
    );
  }
);

export default TagViewSummary;
